import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { getItem, saveItem } from '@/modules/inventory/services/itemRepository';
import { getFirestoreDb } from '@/services/firebase';
import {
  buildTimestampPayload,
  timestampToDate,
} from '@/modules/inventory/services/firestoreMapper';
import { MAINTENANCE_TASKS_COLLECTION } from '@/modules/maintenance/services/constants';
import {
  assertMaintenanceTransition,
  isBlockingMaintenanceStatus,
} from '@/modules/maintenance/utils/maintenanceStatusMachine';
import {
  isDateRangeOverlapping,
  isValidMaintenanceRange,
} from '@/modules/maintenance/utils/dateOverlap';
import {
  itemStatusAfterMaintenanceComplete,
  itemStatusForActiveMaintenance,
} from '@/modules/maintenance/utils/itemStatusSync';
import type {
  MaintenanceFilters,
  MaintenanceStatus,
  MaintenanceTask,
  MaintenanceTaskInput,
  MaintenanceType,
} from '@/modules/maintenance/types/maintenance';
import { listReservationsInRange } from '@/modules/reservations/services/reservationRepository';
import { isBlockingReservation } from '@/modules/reservations/utils/reservationFilters';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import {
  publishMaintenanceCompleted,
  publishMaintenanceConflict,
  publishMaintenanceScheduled,
  publishMaintenanceStarted,
} from '@/services/events/maintenanceEvents';
import { recordMaintenanceMovement } from '@/services/inventoryMovements';
import { withFirestoreError } from '@/shared/utils/firestoreError';
type MaintenanceDoc = {
  companyId: string;
  inventoryItemId: string;
  type: MaintenanceType;
  status: MaintenanceStatus;
  title: string;
  description: string;
  scheduledStart: string;
  scheduledEnd: string;
  completedAt: string | null;
  assignedTo: string;
  notes: string;
  preventiveIntervalDays: number | null;
  nextInspectionDate: string | null;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

function mapTask(id: string, data: MaintenanceDoc): MaintenanceTask {
  return {
    id,
    companyId: data.companyId,
    inventoryItemId: data.inventoryItemId,
    type: data.type,
    status: data.status,
    title: data.title,
    description: data.description ?? '',
    scheduledStart: data.scheduledStart,
    scheduledEnd: data.scheduledEnd,
    completedAt: data.completedAt,
    assignedTo: data.assignedTo ?? '',
    notes: data.notes ?? '',
    preventiveIntervalDays: data.preventiveIntervalDays ?? null,
    nextInspectionDate: data.nextInspectionDate ?? null,
    createdBy: data.createdBy,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

export async function listMaintenanceTasks(
  companyId: string = appConfig.companyId,
): Promise<MaintenanceTask[]> {
  const snap = await getDocs(
    query(
      collection(getFirestoreDb(), MAINTENANCE_TASKS_COLLECTION),
      where('companyId', '==', companyId),
    ),
  );
  return snap.docs
    .map((d) => mapTask(d.id, d.data() as MaintenanceDoc))
    .sort((a, b) => b.scheduledStart.localeCompare(a.scheduledStart));
}

export async function listMaintenanceTasksInRange(
  rangeStart: string,
  rangeEnd: string,
  companyId: string = appConfig.companyId,
): Promise<MaintenanceTask[]> {
  const snap = await getDocs(
    query(
      collection(getFirestoreDb(), MAINTENANCE_TASKS_COLLECTION),
      where('companyId', '==', companyId),
      where('scheduledStart', '<=', rangeEnd),
    ),
  );
  return snap.docs
    .map((d) => mapTask(d.id, d.data() as MaintenanceDoc))
    .filter((t) => t.scheduledEnd >= rangeStart)
    .sort((a, b) => a.scheduledStart.localeCompare(b.scheduledStart));
}

export async function listMaintenanceTasksForItem(
  itemId: string,
  companyId: string = appConfig.companyId,
): Promise<MaintenanceTask[]> {
  const snap = await getDocs(
    query(
      collection(getFirestoreDb(), MAINTENANCE_TASKS_COLLECTION),
      where('companyId', '==', companyId),
      where('inventoryItemId', '==', itemId),
    ),
  );
  return snap.docs
    .map((d) => mapTask(d.id, d.data() as MaintenanceDoc))
    .sort((a, b) => b.scheduledStart.localeCompare(a.scheduledStart));
}

export async function getMaintenanceTask(
  id: string,
): Promise<MaintenanceTask | null> {
  const snap = await getDoc(
    doc(getFirestoreDb(), MAINTENANCE_TASKS_COLLECTION, id),
  );
  if (!snap.exists()) return null;
  return mapTask(snap.id, snap.data() as MaintenanceDoc);
}

export function filterMaintenanceTasks(
  tasks: MaintenanceTask[],
  filters: MaintenanceFilters,
): MaintenanceTask[] {
  let out = tasks;
  if (filters.status && filters.status !== 'all') {
    out = out.filter((t) => t.status === filters.status);
  }
  if (filters.type && filters.type !== 'all') {
    out = out.filter((t) => t.type === filters.type);
  }
  if (filters.inventoryItemId) {
    out = out.filter((t) => t.inventoryItemId === filters.inventoryItemId);
  }
  if (filters.fromDate) {
    out = out.filter((t) => t.scheduledEnd >= filters.fromDate!);
  }
  if (filters.toDate) {
    out = out.filter((t) => t.scheduledStart <= filters.toDate!);
  }
  if (filters.search?.trim()) {
    const q = filters.search.trim().toLowerCase();
    out = out.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.notes.toLowerCase().includes(q),
    );
  }
  return out;
}

async function findBlockingMaintenanceOverlap(
  itemId: string,
  start: string,
  end: string,
  excludeTaskId?: string,
): Promise<MaintenanceTask[]> {
  const tasks = await listMaintenanceTasksForItem(itemId);
  return tasks.filter((t) => {
    if (excludeTaskId && t.id === excludeTaskId) return false;
    if (!isBlockingMaintenanceStatus(t.status)) return false;
    return isDateRangeOverlapping(
      start,
      end,
      t.scheduledStart,
      t.scheduledEnd,
    );
  });
}

async function findReservationOverlap(
  itemId: string,
  start: string,
  end: string,
): Promise<boolean> {
  const reservations = await listReservationsInRange(start, end);
  return reservations.some(
    (r) =>
      r.inventoryItemIds.includes(itemId) &&
      isBlockingReservation(r) &&
      isDateRangeOverlapping(start, end, r.startDate, r.endDate),
  );
}

async function syncItemFromTask(
  task: MaintenanceTask,
  previousStatus?: MaintenanceStatus,
): Promise<void> {
  const item = await getItem(task.inventoryItemId);
  if (!item) return;

  let nextStatus = item.status;
  const target = itemStatusForActiveMaintenance(task.type, task.status);
  if (target) nextStatus = target;
  if (task.status === 'completed' || task.status === 'cancelled') {
    nextStatus = itemStatusAfterMaintenanceComplete(item.status);
  }

  if (nextStatus === item.status) return;

  await saveItem(
    {
      sku: item.sku,
      name: item.name,
      description: item.description,
      categoryId: item.categoryId,
      subcategoryId: item.subcategoryId,
      mode: item.mode,
      status: nextStatus,
      customFieldValues: item.customFieldValues,
      trackStock: item.trackStock,
      stockQuantity: item.stockQuantity,
      stockUnit: item.stockUnit,
    },
    item.id,
  );
  void previousStatus;
}

export async function saveMaintenanceTask(
  input: MaintenanceTaskInput,
  createdBy: string,
  id?: string,
): Promise<MaintenanceTask> {
  return withFirestoreError(async () => {
    if (!isValidMaintenanceRange(input.scheduledStart, input.scheduledEnd)) {
      throw new Error('La fecha de fin debe ser igual o posterior al inicio.');
    }

    const item = await getItem(input.inventoryItemId);
    if (!item) throw new Error('Artículo no encontrado.');

    const maintOverlap = await findBlockingMaintenanceOverlap(
      input.inventoryItemId,
      input.scheduledStart,
      input.scheduledEnd,
      id,
    );
    if (maintOverlap.length > 0 && isBlockingMaintenanceStatus(input.status)) {
      void publishMaintenanceConflict({
        ...(id ? { taskId: id } : {}),
        itemId: input.inventoryItemId,
        start: input.scheduledStart,
        end: input.scheduledEnd,
      });
      throw new Error(
        'Ya existe mantenimiento programado en ese rango para el artículo.',
      );
    }

    const resOverlap = await findReservationOverlap(
      input.inventoryItemId,
      input.scheduledStart,
      input.scheduledEnd,
    );
    if (resOverlap && isBlockingMaintenanceStatus(input.status)) {
      void publishMaintenanceConflict({
        itemId: input.inventoryItemId,
        start: input.scheduledStart,
        end: input.scheduledEnd,
      });
      throw new Error(
        'Hay reservas activas en el rango; no se puede bloquear el artículo.',
      );
    }

    const isNew = !id;
    const ref = id
      ? doc(getFirestoreDb(), MAINTENANCE_TASKS_COLLECTION, id)
      : doc(collection(getFirestoreDb(), MAINTENANCE_TASKS_COLLECTION));
    const existing = isNew ? null : await getDoc(ref);
    const before = existing?.exists()
      ? toAuditSnapshot(
          mapTask(existing!.id, existing!.data() as MaintenanceDoc),
        )
      : undefined;

    if (existing?.exists()) {
      const prev = mapTask(existing.id, existing.data() as MaintenanceDoc);
      if (input.status !== prev.status) {
        assertMaintenanceTransition(prev.status, input.status);
      }
    }

    const payload = {
      companyId: appConfig.companyId,
      inventoryItemId: input.inventoryItemId,
      type: input.type,
      status: input.status,
      title: input.title.trim(),
      description: input.description.trim(),
      scheduledStart: input.scheduledStart,
      scheduledEnd: input.scheduledEnd,
      completedAt:
        input.status === 'completed'
          ? new Date().toISOString().slice(0, 10)
          : null,
      assignedTo: input.assignedTo.trim(),
      notes: input.notes.trim(),
      preventiveIntervalDays: input.preventiveIntervalDays ?? null,
      nextInspectionDate: input.nextInspectionDate ?? null,
      createdBy: existing?.exists()
        ? (existing.data() as MaintenanceDoc).createdBy
        : createdBy,
      ...buildTimestampPayload(isNew),
    };

    await setDoc(ref, payload, { merge: true });
    const saved = await getDoc(ref);
    if (!saved.exists()) throw new Error('No se pudo guardar la tarea.');
    const result = mapTask(saved.id, saved.data() as MaintenanceDoc);

    recordEntityWrite({
      entityType: 'maintenanceTask',
      entityId: result.id,
      isCreate: isNew,
      ...(before !== undefined ? { before } : {}),
      after: toAuditSnapshot(result),
    });

    await syncItemFromTask(
      result,
      existing?.exists()
        ? (existing.data() as MaintenanceDoc).status
        : undefined,
    );

    if (isNew && isBlockingMaintenanceStatus(result.status)) {
      void publishMaintenanceScheduled(result);
    }
    if (
      existing?.exists() &&
      (existing.data() as MaintenanceDoc).status !== 'in_progress' &&
      result.status === 'in_progress'
    ) {
      void publishMaintenanceStarted(result);
    }
    if (
      existing?.exists() &&
      (existing.data() as MaintenanceDoc).status !== 'completed' &&
      result.status === 'completed'
    ) {
      void publishMaintenanceCompleted(result);
    }

    const prevStatus = existing?.exists()
      ? (existing.data() as MaintenanceDoc).status
      : null;
    if (prevStatus !== 'in_progress' && result.status === 'in_progress') {
      void recordMaintenanceMovement(
        result.inventoryItemId,
        result.id,
        'out',
        result.type === 'repair' ? 'repair' : 'maintenance',
        result.createdBy,
      ).catch(() => undefined);
    }
    if (prevStatus !== 'completed' && result.status === 'completed') {
      void recordMaintenanceMovement(
        result.inventoryItemId,
        result.id,
        'in',
        result.type === 'repair' ? 'repair' : 'maintenance',
        result.createdBy,
      ).catch(() => undefined);
    }

    return result;
  }, 'maintenance');
}

export async function listBlockingMaintenanceForAvailability(
  itemIds: string[],
  startDate: string,
  endDate: string,
  excludeTaskId?: string,
): Promise<MaintenanceTask[]> {
  const all = await listMaintenanceTasksInRange(startDate, endDate);
  const itemSet = new Set(itemIds);
  return all.filter((t) => {
    if (!itemSet.has(t.inventoryItemId)) return false;
    if (excludeTaskId && t.id === excludeTaskId) return false;
    if (!isBlockingMaintenanceStatus(t.status)) return false;
    return isDateRangeOverlapping(
      startDate,
      endDate,
      t.scheduledStart,
      t.scheduledEnd,
    );
  });
}
